import { Container } from '@mui/material';
import type { FC } from 'react';
import React, { useMemo, useState } from 'react';
import {
  Card,
  Divider,
  HeaderBreadcrumbs,
  Label,
  NumberLabel,
  Tab,
  Tabs,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableEmptyRows,
  TableHeadCustom,
  TableRow,
  TableSkeleton,
  useFileUrlToTypeMapping,
  useTable,
  type HeadCell,
} from '@pflegenavi/web-components';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { fmAppMainPages } from '@pflegenavi/frontend/routing';
import { useInitializeFM } from '../hooks/useInitializeFM';
import { useLocale, useFormatting } from '@pflegenavi/frontend/localization';
import { YEAR_MONTH_DAY_SHORT_FORMAT } from '@pflegenavi/shared/constants';
import { useFamilyMemberReceiptsPhoenix } from '@pflegenavi/frontend/api-nursing-home';
import { ReceiptState } from '@pflegenavi/shared/api';
import {
  EnlargeImageGallery,
  IconTooltip,
} from '@pflegenavi/transaction-components';
import { useUrlsToImages } from '@pflegenavi/shared-frontend/platform';
import { useReceiptImages } from '@pflegenavi/frontend/api-nursing-home';

enum SettledReceiptColumn {
  SequenceID = 'sequenceId',
  ReceiptType = 'receiptType',
  ServiceProvider = 'serviceProvider',
  Amount = 'amount',
  SettlementAmount = 'settlementAmount',
  Date = 'date',
  Receipt = 'receipt',
}

export const SettledReceiptsPage: FC = () => {
  const { t } = useTranslation();
  const locale = useLocale();
  const { fCurrency } = useFormatting();
  const theme = useTheme();
  const { selectedResident } = useInitializeFM();

  const { data: receiptsData, isLoading } = useFamilyMemberReceiptsPhoenix(
    selectedResident?.residentId ?? '',
    selectedResident?.tenant ?? ''
  );

  const settledReceipts = useMemo(() => {
    if (!receiptsData?.data) {
      return [];
    }
    return receiptsData.data.filter(
      (r) => r.receipt_state === ReceiptState.Settled
    );
  }, [receiptsData]);

  const table = useTable<SettledReceiptColumn>({
    tableKey: 'familyMemberTransactions',
    defaultOrder: 'desc',
    defaultOrderBy: SettledReceiptColumn.Date,
    defaultCurrentPage: 0,
    defaultRowsPerPage: 10,
    sortKeys: {
      [SettledReceiptColumn.SequenceID]: ['id'],
      [SettledReceiptColumn.ReceiptType]: ['receipt_type.name'],
      [SettledReceiptColumn.ServiceProvider]: ['service_provider.name'],
      [SettledReceiptColumn.Amount]: ['amount'],
      [SettledReceiptColumn.SettlementAmount]: ['settlement_amount'],
      [SettledReceiptColumn.Date]: ['receipt_date', 'inserted_at', 'id'],
      [SettledReceiptColumn.Receipt]: ['receipt_image_ids'],
    },
  });

  const [receiptImageIds, setReceiptImageIds] = useState<string[] | undefined>(
    undefined
  );

  const { receiptImages: receiptImageUrls, ImageUrlsLoading } = useUrlsToImages(
    {
      receiptImageIds,
      useReceiptImages,
    }
  );
  const imageUrlToTypeMap = useFileUrlToTypeMapping(receiptImageUrls);

  const rowsOnPage = useMemo(() => {
    const start = table.page * table.rowsPerPage;
    const end = start + table.rowsPerPage;
    const data = [...settledReceipts].sort((a, b) =>
      table.comparator(a as any, b as any)
    );
    return data.slice(start, end);
  }, [settledReceipts, table]);

  const TABLE_HEAD: Array<HeadCell<SettledReceiptColumn>> = [
    { id: SettledReceiptColumn.SequenceID, label: 'ID', align: 'left' },
    {
      id: SettledReceiptColumn.ReceiptType,
      label: t('receipts.type'),
      align: 'left',
      width: 160,
      maxWidth: 160,
    },
    {
      id: SettledReceiptColumn.ServiceProvider,
      label: t('receipts.serviceProvider'),
      align: 'left',
      width: 220,
      maxWidth: 220,
    },
    {
      id: SettledReceiptColumn.Amount,
      label: t('common.amount'),
      align: 'right',
      width: 120,
      maxWidth: 120,
    },
    {
      id: SettledReceiptColumn.SettlementAmount,
      label: t('receipts.settlement.settled-amount'),
      align: 'right',
      width: 250,
      minWidth: 250,
      maxWidth: 250,
    },
    {
      id: SettledReceiptColumn.Date,
      label: t('common.date'),
      align: 'left',
      width: 120,
      maxWidth: 120,
    },
    {
      id: SettledReceiptColumn.Receipt,
      label: t('resident.transactions.table.receipt'),
      align: 'center',
    },
  ];

  return (
    <Container sx={{ maxWidth: 1400 }}>
      <HeaderBreadcrumbs
        heading={t('nav.receipts.settled')}
        links={[
          { name: t('receipts.main'), href: fmAppMainPages.TRANSACTIONS },
          { name: t('nav.receipts.settled') },
        ]}
        sx={{ pb: 0 }}
      />

      <Card>
        <Tabs
          allowScrollButtonsMobile
          variant="scrollable"
          scrollButtons="auto"
          value={'settled'}
          sx={{ px: 2, bgcolor: 'background.neutral' }}
        >
          <Tab
            disableRipple
            key={'settled'}
            value={'settled'}
            label={
              <Stack spacing={1} direction="row" alignItems="center">
                <div>{t('nav.receipts.settled')}</div>
                <NumberLabel color="info" variant="ghost">
                  {settledReceipts.length}
                </NumberLabel>
              </Stack>
            }
          />
          <Stack sx={{ flex: 1 }} />
        </Tabs>
        <Divider />

        <TableContainer sx={{ position: 'relative' }}>
          <Table size="medium">
            <TableHeadCustom
              sx={{ width: 200 }}
              order={table.order}
              orderBy={table.orderBy}
              headLabels={TABLE_HEAD}
              rowCount={settledReceipts.length}
              onSort={table.onSort}
            />

            <TableBody>
              {isLoading &&
                Array.from(Array(table.rowsPerPage).keys()).map(
                  (row: number) => (
                    <TableSkeleton
                      key={`row- ${row}`}
                      colSpan={TABLE_HEAD.length}
                    />
                  )
                )}
            </TableBody>

            <TableBody
              sx={{
                '& .MuiTableRow-root:last-child': {
                  borderBottomWidth: 0,
                },
              }}
            >
              {rowsOnPage.map((receipt) => (
                <TableRow key={receipt.id} hover>
                  <TableCell
                    align="left"
                    sx={{
                      maxWidth: 182,
                      color: 'text.secondary',
                      fontSize: 13,
                    }}
                  >
                    {'--'}
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{
                      maxWidth: 160,
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                    }}
                  >
                    {t(`receiptType.${receipt.receipt_type?.name ?? 'Andere'}`)}
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{
                      maxWidth: 220,
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                    }}
                  >
                    {receipt.service_provider?.name ?? '--'}
                  </TableCell>
                  <TableCell align="right">
                    <Label variant="ghost" color={'default'}>
                      {fCurrency(receipt.amount / 100)}
                    </Label>
                  </TableCell>
                  <TableCell align="right">
                    <Label variant="ghost" color={'info'}>
                      {fCurrency((receipt.settlement_amount ?? 0) / 100)}
                    </Label>
                  </TableCell>
                  <TableCell align="left">
                    {(
                      receipt.settlement_date ?? receipt.inserted_at
                    ).toLocaleDateString(locale, YEAR_MONTH_DAY_SHORT_FORMAT)}
                  </TableCell>
                  <TableCell
                    align="center"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                  >
                    {receipt.receipt_image_ids &&
                    receipt.receipt_image_ids.length > 0 ? (
                      <IconTooltip
                        title={t('tooltip.show-receipt-image')}
                        color={theme.palette.success.main}
                        icon="check"
                        onClick={() =>
                          setReceiptImageIds(receipt.receipt_image_ids)
                        }
                      />
                    ) : (
                      <IconTooltip
                        title={t('tooltip.no-image-available')}
                        color={theme.palette.grey['400']}
                        icon="remove"
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}

              {!isLoading && (
                <EnlargeImageGallery
                  loading={ImageUrlsLoading}
                  open={Boolean(receiptImageIds)}
                  handleClose={() => setReceiptImageIds(undefined)}
                  initialSelectedIndex={0}
                  imageUrls={receiptImageUrls || []}
                  imageUrlToTypeMap={imageUrlToTypeMap}
                />
              )}

              {settledReceipts && settledReceipts.length > 0 && (
                <TableEmptyRows
                  height={72}
                  emptyRows={Math.max(
                    0,
                    ((1 + table.page) * table.rowsPerPage -
                      settledReceipts.length) %
                      table.rowsPerPage
                  )}
                />
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Container>
  );
};
